<template>
  <v-row justify="center" class="py-4 px-2">
    <v-col cols="12" sm="10" md="8" lg="6" class="payment-wrapper">
      <v-card elevation="3" class="rounded-lg overflow-hidden">
        <!-- Brand Header -->
        <div class="brand-header pa-5 d-flex flex-column align-center justify-center text-center">
          <img
            v-if="company.logo"
            :src="company.logo"
            alt="Logo"
            class="tenant-logo mb-2"
          />
          <v-avatar v-else color="primary" size="52" class="mb-2">
            <v-icon dark size="30">mdi-domain</v-icon>
          </v-avatar>
          <h1 class="text-h5 font-weight-bold mb-1">
            {{ company.name || 'Betalingsoverzicht' }}
          </h1>
          <span class="text-caption grey--text text--lighten-1">
            Factuur referentie: {{ invoice.invoiceNumber || 'Onbekend' }}
          </span>
        </div>

        <!-- Migration Notice Banner -->
        <v-alert
          border="left"
          colored-border
          type="info"
          elevation="0"
          class="ma-4 mb-3 migration-banner rounded"
        >
          <div class="d-flex align-start">
            <div>
              <div class="subtitle-2 font-weight-bold primary--text mb-1 d-flex align-center">
                <v-icon small color="primary" class="mr-1">mdi-swap-horizontal-bold</v-icon>
                Overstap naar Bonbon v3 &mdash; Betaalinformatie
              </div>
              <p class="body-2 mb-1 text--secondary">
                Wij zijn overgestapt naar een nieuw softwaresysteem (<strong>Bonbon v3</strong>).
                Hierdoor zijn eerdere iDEAL / Mollie QR-codes buiten werking gesteld en is direct online
                afrekenen via deze link niet meer actief.
              </p>
              <p class="body-2 mb-0 text--secondary">
                U kunt uw factuur eenvoudig voldoen via uw eigen bankieren app of internetbankieren
                met de onderstaande betaalgegevens.
              </p>
            </div>
          </div>
        </v-alert>

        <!-- Loading State -->
        <div v-if="loading" class="d-flex flex-column align-center justify-center py-12">
          <v-progress-circular indeterminate color="primary" size="48" class="mb-3"></v-progress-circular>
          <span class="body-2 text--secondary">Factuurgegevens ophalen...</span>
        </div>

        <!-- Error State -->
        <v-card-text v-else-if="errorMessage" class="text-center py-8">
          <v-icon size="56" color="warning" class="mb-3">mdi-alert-circle-outline</v-icon>
          <div class="text-h6 font-weight-bold mb-2">Factuur niet beschikbaar</div>
          <p class="body-2 text--secondary mb-4">{{ errorMessage }}</p>
          <div v-if="company.phone || company.email" class="caption text--secondary">
            Neem bij vragen contact op met de leverancier:
            <div v-if="company.phone" class="font-weight-medium mt-1">
              Tel: <a :href="'tel:' + company.phone">{{ company.phone }}</a>
            </div>
            <div v-if="company.email">
              E-mail: <a :href="'mailto:' + company.email">{{ company.email }}</a>
            </div>
          </div>
        </v-card-text>

        <!-- Main Content -->
        <v-card-text v-else class="pa-4 pt-0">
          <!-- Paid Notice -->
          <v-alert
            v-if="isPaid"
            type="success"
            text
            icon="mdi-check-decagram"
            class="mb-4 rounded"
          >
            <div class="subtitle-2 font-weight-bold">Deze factuur is reeds voldaan</div>
            <div class="body-2">
              Er staat geen openstaand bedrag meer open voor factuur
              <strong>{{ invoice.invoiceNumber }}</strong>. Hartelijk dank voor uw betaling!
            </div>
          </v-alert>

          <!-- Outstanding Amount Card -->
          <div
            v-else
            class="amount-box pa-4 mb-4 rounded-lg d-flex flex-column align-center justify-center text-center"
          >
            <span class="caption text-uppercase font-weight-bold grey--text text--darken-2">
              Openstaand te betalen bedrag
            </span>
            <span class="text-h4 font-weight-bold primary--text my-1">
              {{ toCurrency(invoice.debit) }}
            </span>
            <v-btn
              small
              outlined
              color="primary"
              class="mt-1"
              @click="copyText(invoice.debit.toFixed(2), 'Bedrag')"
            >
              <v-icon small left>mdi-content-copy</v-icon>
              Kopieer bedrag (&euro; {{ invoice.debit.toFixed(2) }})
            </v-btn>
          </div>

          <!-- Invoice Details Overview -->
          <div class="section-title mb-2 d-flex align-center">
            <v-icon small color="primary" class="mr-1">mdi-file-document-outline</v-icon>
            <span class="subtitle-2 font-weight-bold">Factuurgegevens</span>
          </div>

          <v-card outlined class="rounded-lg mb-4">
            <v-list dense class="pa-0">
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle class="caption">Factuurnummer</v-list-item-subtitle>
                  <v-list-item-title class="body-2 font-weight-bold">
                    {{ invoice.invoiceNumber }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn
                    icon
                    small
                    title="Kopieer factuurnummer"
                    @click="copyText(invoice.invoiceNumber, 'Factuurnummer')"
                  >
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-divider></v-divider>

              <v-list-item v-if="clientName">
                <v-list-item-content>
                  <v-list-item-subtitle class="caption">Factuur t.a.v.</v-list-item-subtitle>
                  <v-list-item-title class="body-2">
                    {{ clientName }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider v-if="clientName"></v-divider>

              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle class="caption">Factuurdatum</v-list-item-subtitle>
                  <v-list-item-title class="body-2">
                    {{ toDateString(invoice.date) }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-content>
                  <v-list-item-subtitle class="caption">Vervaldatum</v-list-item-subtitle>
                  <v-list-item-title class="body-2 d-flex align-center">
                    {{ toDateString(invoice.dueDate) }}
                    <v-chip
                      v-if="isOverdue"
                      x-small
                      color="error"
                      text-color="white"
                      class="ml-2 font-weight-bold"
                    >
                      Vervallen
                    </v-chip>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>

          <!-- Tenant Payment Details (Betaalgegevens) -->
          <div class="section-title mb-2 d-flex align-center">
            <v-icon small color="primary" class="mr-1">mdi-bank-outline</v-icon>
            <span class="subtitle-2 font-weight-bold">Betaalgegevens van de leverancier</span>
          </div>

          <v-card outlined class="rounded-lg mb-4 payment-details-card">
            <v-list dense class="pa-0">
              <!-- Account Holder -->
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle class="caption">Rekeninghouder / Begunstigde</v-list-item-subtitle>
                  <v-list-item-title class="body-2 font-weight-bold">
                    {{ accountHolder }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn
                    icon
                    small
                    title="Kopieer naam begunstigde"
                    @click="copyText(accountHolder, 'Naam begunstigde')"
                  >
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-divider></v-divider>

              <!-- Primary IBAN -->
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle class="caption">IBAN</v-list-item-subtitle>
                  <v-list-item-title class="body-2 font-weight-bold font-mono">
                    {{ formatIBAN(company.IBAN1) || '-' }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn
                    icon
                    small
                    color="primary"
                    title="Kopieer IBAN"
                    @click="copyText((company.IBAN1 || '').replace(/\s+/g, ''), 'IBAN')"
                  >
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="company.BIC1"></v-divider>

              <!-- Primary BIC -->
              <v-list-item v-if="company.BIC1">
                <v-list-item-content>
                  <v-list-item-subtitle class="caption">BIC / SWIFT</v-list-item-subtitle>
                  <v-list-item-title class="body-2 font-mono">
                    {{ company.BIC1 }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn
                    icon
                    small
                    title="Kopieer BIC"
                    @click="copyText(company.BIC1, 'BIC')"
                  >
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-divider></v-divider>

              <!-- Payment Reference -->
              <v-list-item class="highlight-reference">
                <v-list-item-content>
                  <v-list-item-subtitle class="caption font-weight-bold primary--text">
                    Betalingskenmerk / Omschrijving
                  </v-list-item-subtitle>
                  <v-list-item-title class="body-1 font-weight-bold">
                    {{ invoice.invoiceNumber }}
                  </v-list-item-title>
                  <span class="caption text--secondary mt-1">
                    Vermeld dit nummer altijd bij de omschrijving van uw betaling.
                  </span>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn
                    color="primary"
                    outlined
                    small
                    @click="copyText(invoice.invoiceNumber, 'Betalingskenmerk')"
                  >
                    <v-icon small left>mdi-content-copy</v-icon>
                    Kopieer
                  </v-btn>
                </v-list-item-action>
              </v-list-item>

              <!-- Secondary IBAN (if configured) -->
              <template v-if="company.IBAN2">
                <v-divider></v-divider>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle class="caption">IBAN 2 (Alternatief)</v-list-item-subtitle>
                    <v-list-item-title class="body-2 font-mono">
                      {{ formatIBAN(company.IBAN2) }}
                      <span v-if="company.BIC2" class="caption text--secondary ml-2">
                        (BIC: {{ company.BIC2 }})
                      </span>
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      small
                      title="Kopieer IBAN 2"
                      @click="copyText((company.IBAN2 || '').replace(/\s+/g, ''), 'IBAN 2')"
                    >
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </template>
            </v-list>
          </v-card>

          <!-- SEPA EPC QR Code (Direct Banking App Scan) -->
          <div v-if="!isPaid && company.IBAN1" class="mb-4">
            <v-expansion-panels flat class="epc-accordion">
              <v-expansion-panel class="rounded-lg border">
                <v-expansion-panel-header class="py-2 px-3">
                  <div class="d-flex align-center">
                    <v-icon small color="primary" class="mr-2">mdi-qrcode-scan</v-icon>
                    <span class="body-2 font-weight-medium">
                      Betalen via mobiel bankieren QR-code (SEPA)
                    </span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="d-flex flex-column align-center text-center pt-2">
                    <canvas ref="epcCanvas" class="epc-canvas mb-2"></canvas>
                    <span class="caption text--secondary max-w-400">
                      Scan deze SEPA QR-code direct met uw mobiele bankieren app
                      (o.a. ING, Rabobank, ABN AMRO, SNS, ASN) om het bedrag en factuurnummer automatisch in te vullen.
                    </span>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>

          <!-- Tenant Contact & Company Information -->
          <v-expansion-panels flat class="contact-accordion mb-2">
            <v-expansion-panel class="rounded-lg border">
              <v-expansion-panel-header class="py-2 px-3">
                <div class="d-flex align-center">
                  <v-icon small color="grey darken-1" class="mr-2">mdi-information-outline</v-icon>
                  <span class="caption font-weight-medium grey--text text--darken-2">
                    Contact &amp; Bedrijfsgegevens {{ company.name ? company.name : 'leverancier' }}
                  </span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="caption text--secondary py-1">
                  <div v-if="company.name" class="font-weight-bold text--primary mb-1">
                    {{ company.name }}
                  </div>
                  <div v-if="company.address">
                    {{ company.address }}
                  </div>
                  <div v-if="company.postalcode || company.city">
                    {{ company.postalcode }} {{ company.city }}
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div v-if="company.phone" class="mb-1">
                    <v-icon x-small class="mr-1">mdi-phone</v-icon>
                    Telefoon: <a :href="'tel:' + company.phone">{{ company.phone }}</a>
                  </div>
                  <div v-if="company.email" class="mb-1">
                    <v-icon x-small class="mr-1">mdi-email</v-icon>
                    E-mail: <a :href="'mailto:' + company.email">{{ company.email }}</a>
                  </div>
                  <div v-if="company.website" class="mb-1">
                    <v-icon x-small class="mr-1">mdi-web</v-icon>
                    Website: <a :href="company.website" target="_blank" rel="noopener noreferrer">{{ company.website }}</a>
                  </div>
                  <div v-if="company.COCNumber" class="mb-1">
                    KvK-nummer: {{ company.COCNumber }}
                  </div>
                  <div v-if="company.VATNumber">
                    BTW-nummer: {{ company.VATNumber }}
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Copy Feedback Snackbar -->
    <v-snackbar
      v-model="snackbar"
      timeout="2200"
      bottom
      rounded="pill"
      color="grey darken-4"
    >
      <div class="d-flex align-center justify-center font-weight-medium">
        <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
        {{ snackText }}
      </div>
    </v-snackbar>
  </v-row>
</template>

<script>
import QRCode from 'qrcode'
import { config } from '../config/config'
import { toCurrency, toDateString } from '../js/functions'

export default {
  name: 'CreatePayment',
  data() {
    return {
      invoice: {
        name: '',
        invoiceNumber: '',
        date: '',
        dueDate: '',
        debit: 0,
        grossTotal: 0,
        client: null
      },
      company: {
        name: '',
        address: '',
        postalcode: '',
        city: '',
        phone: '',
        email: '',
        website: '',
        COCNumber: '',
        VATNumber: '',
        IBAN1: '',
        BIC1: '',
        accountHolder1: '',
        IBAN2: '',
        BIC2: '',
        accountHolder2: '',
        logo: ''
      },
      loading: true,
      errorMessage: '',
      snackbar: false,
      snackText: ''
    }
  },
  computed: {
    isPaid() {
      return Boolean(this.invoice._id && this.invoice.debit === 0)
    },
    isOverdue() {
      if (!this.invoice || !this.invoice.dueDate || !(this.invoice.debit > 0)) return false
      return new Date(this.invoice.dueDate) < new Date()
    },
    clientName() {
      if (this.invoice.client && this.invoice.client.name) return this.invoice.client.name
      return this.invoice.name || ''
    },
    accountHolder() {
      return (
        this.company.accountHolder1 ||
        this.company.name ||
        'Leverancier'
      )
    }
  },
  methods: {
    toDateString(date) {
      if (!date) return '-'
      return toDateString(date)
    },
    toCurrency(val) {
      if (val === undefined || val === null) return '€ 0,00'
      return toCurrency(val)
    },
    formatIBAN(iban) {
      if (!iban) return ''
      const clean = String(iban).replace(/\s+/g, '').toUpperCase()
      return clean.replace(/(.{4})/g, '$1 ').trim()
    },
    copyText(text, label) {
      if (!text) return
      const clean = String(text).trim()
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(clean)
          .then(() => this.showCopySnack(label))
          .catch(() => this.fallbackCopy(clean, label))
      } else {
        this.fallbackCopy(clean, label)
      }
    },
    fallbackCopy(text, label) {
      try {
        const el = document.createElement('textarea')
        el.value = text
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        this.showCopySnack(label)
      } catch (e) {
        console.warn('Copying failed:', e)
      }
    },
    showCopySnack(label) {
      this.snackText = `${label} gekopieerd!`
      this.snackbar = true
    },
    async fetchInvoice(_id) {
      this.loading = true
      this.errorMessage = ''
      try {
        const data = JSON.stringify({ _id: _id })
        const res = await fetch(`${config.payments.host}/fetchInvoice/${data}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store'
        })
        if (!res.ok) {
          throw new Error(`Server returned HTTP ${res.status}`)
        }
        const response = await res.json()
        if (response && response.length > 0) {
          this.invoice = Object.assign({}, this.invoice, response[0])
          if (this.invoice.company) {
            this.company = Object.assign({}, this.company, this.invoice.company)
          }
        } else {
          this.errorMessage = 'Factuur kon niet worden gevonden. Mogelijk is de link onvolledig of verlopen.'
        }

        if (!this.company.IBAN1) {
          await this.fetchCompanyFallback()
        }
      } catch (e) {
        console.error('Fout bij ophalen van factuur:', e)
        this.errorMessage = 'Kan de factuurgegevens momenteel niet laden. Controleer uw verbinding of neem contact op.'
        await this.fetchCompanyFallback()
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.generateEPCQR()
        })
      }
    },
    async fetchCompanyFallback() {
      try {
        const res = await fetch(`${config.payments.host}/company`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store'
        })
        if (res.ok) {
          const comp = await res.json()
          if (comp && (comp.name || comp.IBAN1)) {
            this.company = Object.assign({}, this.company, comp)
          }
        }
      } catch (e) {
        console.warn('Company fallback could not be fetched:', e)
      }
    },
    generateEPCQR() {
      if (!this.$refs.epcCanvas) return
      if (!this.invoice || !this.company || !this.company.IBAN1 || !(this.invoice.debit > 0)) {
        return
      }

      try {
        const iban = (this.company.IBAN1 || '').replace(/\s+/g, '').toUpperCase()
        const bic = (this.company.BIC1 || '').replace(/\s+/g, '').toUpperCase()
        const name = (this.company.accountHolder1 || this.company.name || '').substring(0, 70)
        const amount = Number(this.invoice.debit).toFixed(2)
        const ref = String(this.invoice.invoiceNumber || '').substring(0, 140)

        // SEPA EPC Quick Response Code standard
        const payload = [
          'BCD',
          '002',
          '1',
          'SCT',
          bic,
          name,
          iban,
          `EUR${amount}`,
          '',
          '',
          ref
        ].join('\n')

        QRCode.toCanvas(this.$refs.epcCanvas, payload, {
          width: 170,
          margin: 1,
          color: { dark: '#0f172a', light: '#ffffff' }
        }, (err) => {
          if (err) console.warn('Could not generate EPC QR code:', err)
        })
      } catch (err) {
        console.warn('EPC QR generator error:', err)
      }
    }
  },
  async mounted() {
    await this.fetchInvoice(this.$route.params.id)
  }
}
</script>

<style scoped>
.payment-wrapper {
  max-width: 620px;
}
.brand-header {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: #ffffff;
}
.tenant-logo {
  max-height: 52px;
  max-width: 180px;
  object-fit: contain;
}
.migration-banner {
  background-color: #f0f7ff !important;
  border-left-width: 5px !important;
}
.amount-box {
  background: linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%);
  border: 1px solid #e2e8f0;
}
.font-mono {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  letter-spacing: 0.5px;
}
.highlight-reference {
  background-color: #f8fafc;
}
.border {
  border: 1px solid #e2e8f0;
}
.epc-canvas {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.max-w-400 {
  max-width: 400px;
}
</style>